// Reusable single-select searchable dropdown ("combobox") that replaces plain
// entity <select>s. Renders its popover through a portal to document.body so it
// is never clipped by scrollable tables / board columns; positioned under the
// trigger via getBoundingClientRect() (fixed positioning).
import * as React from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, Plus } from "lucide-react";
import { cn } from "@/lib/utils";

export type ComboboxOption = {
  value: string;
  label: string;
  hint?: string;
  keywords?: string;
};

export function Combobox({
  value,
  onChange,
  options,
  ariaLabel,
  className,
  allowEmpty,
  emptyLabel = "—",
  placeholder,
  compact,
  onCreateNew,
}: {
  value: string;
  onChange: (v: string) => void;
  options: ComboboxOption[];
  ariaLabel?: string;
  className?: string;
  allowEmpty?: boolean;
  emptyLabel?: string;
  placeholder?: string;
  compact?: boolean;
  // When provided, typing a query that doesn't match any existing option shows
  // a "+ Create new '<query>'" row. Selecting it invokes this callback (the
  // caller is responsible for opening its own creation modal/flow and, once the
  // entity is created, calling onChange(newId) to select it).
  onCreateNew?: (query: string) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState("");
  const [highlightedIndex, setHighlightedIndex] = React.useState(0);
  const [pos, setPos] = React.useState<{ top?: number; bottom?: number; left: number; width: number } | null>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const searchRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);

  const selected = options.find((o) => o.value === value);

  const s = q.trim().toLowerCase();
  const filtered = s
    ? options.filter((o) => `${o.label} ${o.hint ?? ""} ${o.keywords ?? ""}`.toLowerCase().includes(s))
    : options;

  const showEmptyOption = allowEmpty && (!s || emptyLabel.toLowerCase().includes(s));

  const trimmedQ = q.trim();
  const exactMatch = trimmedQ && options.some((o) => o.label.toLowerCase() === trimmedQ.toLowerCase());
  const showCreateNew = !!onCreateNew && !!trimmedQ && !exactMatch;

  const totalItems = (showEmptyOption ? 1 : 0) + filtered.length + (showCreateNew ? 1 : 0);

  const updatePos = React.useCallback(() => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    if (!r || (r.width === 0 && r.height === 0)) return;

    const menuWidth = Math.max(r.width, 220);
    const left = Math.max(8, Math.min(r.left, window.innerWidth - menuWidth - 8));
    const spaceBelow = window.innerHeight - r.bottom;
    const spaceAbove = r.top;
    if (spaceBelow < 240 && spaceAbove > 180) {
      setPos({ bottom: window.innerHeight - r.top + 4, left, width: r.width });
    } else {
      setPos({ top: r.bottom + 4, left, width: r.width });
    }
  }, []);

  const openMenu = (initialQ?: string) => {
    updatePos();

    if (initialQ !== undefined) {
      setQ(initialQ);
      setHighlightedIndex(0);
    } else if (value) {
      const idx = options.findIndex((o) => o.value === value);
      setHighlightedIndex(idx >= 0 ? (allowEmpty ? idx + 1 : idx) : 0);
      setQ("");
    } else {
      setHighlightedIndex(0);
      setQ("");
    }

    setOpen(true);
  };

  // Auto-focus and select-all text in search input when opened
  React.useEffect(() => {
    if (!open) return;
    const raf = requestAnimationFrame(() => {
      if (searchRef.current) {
        searchRef.current.focus({ preventScroll: true });
        searchRef.current.select();
      }
    });
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        setOpen(false);
      }
    };
    const onScroll = (e: Event) => {
      if (menuRef.current?.contains(e.target as Node)) return;
      updatePos();
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", updatePos);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", updatePos);
    };
  }, [open, updatePos]);

  // Reset highlight when filter changes
  React.useEffect(() => {
    setHighlightedIndex(0);
  }, [q]);

  // Scroll highlighted item into view inside the list container without scrolling outer containers
  React.useEffect(() => {
    if (!open || !listRef.current) return;
    const itemEl = listRef.current.querySelector(`[data-index="${highlightedIndex}"]`) as HTMLElement;
    if (itemEl && listRef.current) {
      const itemTop = itemEl.offsetTop;
      const itemBottom = itemTop + itemEl.offsetHeight;
      const listScrollTop = listRef.current.scrollTop;
      const listHeight = listRef.current.clientHeight;
      if (itemTop < listScrollTop) {
        listRef.current.scrollTop = itemTop;
      } else if (itemBottom > listScrollTop + listHeight) {
        listRef.current.scrollTop = itemBottom - listHeight;
      }
    }
  }, [highlightedIndex, open]);

  const selectIndex = (index: number) => {
    if (totalItems === 0) return;
    let target = index;
    if (showEmptyOption) {
      if (target === 0) {
        onChange("");
        setOpen(false);
        return;
      }
      target -= 1;
    }
    if (target < filtered.length) {
      onChange(filtered[target].value);
      setOpen(false);
      return;
    }
    if (showCreateNew) {
      setOpen(false);
      onCreateNew?.(trimmedQ);
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (totalItems > 0) {
        setHighlightedIndex((i) => (i + 1) % totalItems);
      }
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (totalItems > 0) {
        setHighlightedIndex((i) => (i - 1 + totalItems) % totalItems);
      }
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      selectIndex(highlightedIndex);
      return;
    }
    if (e.key === " " && q === "") {
      // Space on empty search selects/toggles current item
      e.preventDefault();
      selectIndex(highlightedIndex);
      return;
    }
  };

  const handleTriggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openMenu();
      return;
    }
    if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
      e.preventDefault();
      openMenu(e.key);
    }
  };

  const displayLabel = selected?.label || placeholder || emptyLabel;

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        aria-label={ariaLabel ?? "Select"}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          if (open) setOpen(false);
          else openMenu();
        }}
        onKeyDown={handleTriggerKeyDown}
        className={cn(
          "flex w-full items-center gap-1 rounded-md border border-input bg-secondary outline-none",
          "focus-visible:ring-2 focus-visible:ring-ring cursor-pointer max-w-full",
          compact ? "h-7 px-1.5 text-xs" : "h-9 px-3 text-sm",
          className
        )}
      >
        <span className={cn("truncate flex-1 text-left", !selected && "text-muted-foreground")}>
          {displayLabel}
        </span>
        <ChevronDown size={compact ? 12 : 14} className="text-muted-foreground shrink-0" aria-hidden="true" />
      </button>

      {open && pos &&
        createPortal(
          <div
            ref={menuRef}
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "fixed",
              ...(pos.top != null ? { top: pos.top } : { bottom: pos.bottom }),
              left: pos.left,
              width: Math.max(pos.width, 220),
              maxWidth: "calc(100vw - 16px)",
              zIndex: 1000,
            }}
            className="rounded-md border border-border bg-card shadow-2xl p-1.5"
          >
            <input
              ref={searchRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onFocus={(e) => e.target.select()}
              onKeyDown={handleInputKeyDown}
              placeholder="Search..."
              aria-label={`Search ${ariaLabel ?? "options"}`}
              className="mb-1 h-8 w-full rounded-md border border-input bg-secondary px-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            <div ref={listRef} role="listbox" className="max-h-[13rem] overflow-y-auto">
              {showEmptyOption && (
                <button
                  type="button"
                  data-index="0"
                  role="option"
                  aria-selected={highlightedIndex === 0}
                  onClick={() => {
                    onChange("");
                    setOpen(false);
                  }}
                  onMouseEnter={() => setHighlightedIndex(0)}
                  className={cn(
                    "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-muted-foreground cursor-pointer",
                    highlightedIndex === 0 ? "bg-accent text-foreground" : "hover:bg-accent"
                  )}
                >
                  <span className="w-3.5 shrink-0">{!value && <Check size={13} aria-hidden="true" />}</span>
                  {emptyLabel}
                </button>
              )}
              {filtered.map((o, idx) => {
                const itemIndex = (showEmptyOption ? 1 : 0) + idx;
                const isHighlighted = highlightedIndex === itemIndex;
                const isSelected = o.value === value;
                return (
                  <button
                    key={o.value}
                    type="button"
                    data-index={itemIndex}
                    role="option"
                    aria-selected={isHighlighted}
                    onClick={() => {
                      onChange(o.value);
                      setOpen(false);
                    }}
                    onMouseEnter={() => setHighlightedIndex(itemIndex)}
                    className={cn(
                      "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm cursor-pointer",
                      isHighlighted ? "bg-accent text-foreground" : "hover:bg-accent"
                    )}
                  >
                    <span className="w-3.5 shrink-0">{isSelected && <Check size={13} aria-hidden="true" />}</span>
                    <span className="truncate flex-1">{o.label}</span>
                    {o.hint && <span className="shrink-0 text-[11px] text-muted-foreground">{o.hint}</span>}
                  </button>
                );
              })}
              {filtered.length === 0 && !showCreateNew && (
                <div className="px-2 py-2 text-xs text-muted-foreground">No match.</div>
              )}
              {showCreateNew && (
                <button
                  type="button"
                  data-index={(showEmptyOption ? 1 : 0) + filtered.length}
                  role="option"
                  aria-selected={highlightedIndex === (showEmptyOption ? 1 : 0) + filtered.length}
                  onClick={() => {
                    setOpen(false);
                    onCreateNew?.(trimmedQ);
                  }}
                  onMouseEnter={() => setHighlightedIndex((showEmptyOption ? 1 : 0) + filtered.length)}
                  className={cn(
                    "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm cursor-pointer text-primary",
                    highlightedIndex === (showEmptyOption ? 1 : 0) + filtered.length
                      ? "bg-accent font-medium"
                      : "hover:bg-accent"
                  )}
                >
                  <Plus size={13} className="shrink-0" aria-hidden="true" />
                  <span className="truncate">Create new "{trimmedQ}"</span>
                </button>
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
